const DEBUG_FLAG_PREFIX = 'gmi-debug-';
const DEBUG_ALL_FLAG = `${DEBUG_FLAG_PREFIX}all`;

/**
 * Logs to the current runtime's console when its feature flag is enabled.
 *
 * Client flags are set in localStorage and logs appear in the browser console.
 *
 * Server flags are set in environment variables and log messages appear
 * in the server process output.  The primary use case for server logging
 * will be for local debugging.
 *
 * Client and server flags are independent.
 *
 * Sample logging call:
 * - debugLog('analytics', eventData);
 * - Messages will be logged when the corresponding debug flag is enabled.
 *
 *  Available flags:
 *  - gmi-debug-analytics (client), GMI_DEBUG_ANALYTICS (server)
 *  - gmi-debug-all (client), GMI_DEBUG_ALL (server)
 *
 * To enable logging
 * - Browser:  Set localStorage['gmi-debug-{feature}'] = 'true' in DevTools console.
 * - Server: Add GMI_DEBUG_{feature}=true server environment variable (not NEXT_PUBLIC_).
 */
export function debugLog(feature: string, ...details: unknown[]): void {
  const normalizedFeature = feature
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (!normalizedFeature) return;

  const isClient = typeof window !== 'undefined';
  if (!isClient && typeof process === 'undefined') return;

  try {
    const environmentFeature = `GMI_DEBUG_${normalizedFeature.replace(/-/g, '_').toUpperCase()}`;
    const isEnabled = isClient
      ? window.localStorage.getItem(`${DEBUG_FLAG_PREFIX}${normalizedFeature}`) === 'true' ||
        window.localStorage.getItem(DEBUG_ALL_FLAG) === 'true'
      : process.env[environmentFeature]?.toLowerCase() === 'true' ||
        process.env.GMI_DEBUG_ALL?.toLowerCase() === 'true';

    if (isEnabled) {
      console.debug(`[${DEBUG_FLAG_PREFIX}${normalizedFeature}]`, ...details);
    }
  } catch {
    // Debug logging must not affect application behavior.
  }
}
